import React, { PureComponent } from "react";
import ReactEcharts from "echarts-for-react";
import { ChartProps, ColorProps, GlobalColorPalette, Chart, Color, ITable, ICSS, ICSSProps } from "@gago/frame";
import sunny from "./img/sun.svg";
import cloudy from "./img/cloud.svg";
import cloudySun from "./img/cloudy-sun.svg";
import rainLight from "./img/rain-light.svg";
import rainHeavy from "./img/rain-heavy.svg";
import typhoon from "./img/typhoon.svg";
import snow from "./img/snow.svg";
import sandStorm from "./img/sand-storm.svg";
import rainMedium from "./img/rain-moderate.svg";
import precipitation from "./img/precipitation.svg";
import precipitation01 from "./img/precipitation-01.svg";
import precipitation02 from "./img/precipitation-02.svg";
import precipitation03 from "./img/precipitation-03.svg";
import precipitation04 from "./img/precipitation-04.svg";
import styled from "styled-components";

// tslint:disable: variable-name jsx-no-multiline-js
const WeatherChartBody = styled.div<{colorPalette: GlobalColorPalette}>`
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  .weather-border3{
    /* width: max-content; */
    height: 100%;
    position: relative;
    display: flex;
    justify-content: flex-start;
    .wb-item{
      width: 62px;
      flex: 1 1 auto;
      color: #00cae0;
      pointer-events: none;
      /* border-right: 1px solid rgba(15, 41, 61, 0.09); */
      &:first-of-type{
        /* border-left: 1px solid rgba(15, 41, 61, 0.09); */
      }
      &:nth-of-type(2n-1){
        background-color: rgba(12, 212, 213, 0.05);
      }
      &> span{
        display: block;
        text-align: center;
        line-height: 1;
      }
      .week{
        font-size: 10px;
      }
      .date{
        margin-top: 6px;
        font-size: 14px;
        font-weight: 500;
      }
      .w-icon{
        margin-top: 10px;
        font-size: 20px;
      }
      .tam{
        margin-top: 10px;
        font-size: 14px;
      }
      .chart-height{
        margin-top: 72px;
      }
      .pre-label{
        margin-top: 10px;
        font-size: 22px;
      }
      .w-img{
        height: 20px;
        width: 20px;
      }
      .w-label{
        margin-top: 10px;
        font-size: 12px;
      }
      .w-value{
        margin-top: 10px;
        font-size: 12px;
      }
    }
    .weather-chart{
      position: absolute;
      width: 100%;
      height: 72px;
      top: 82px;
      left: 0;
    }
  }
`;

// 判断天气的类型
function judgeWeatherBrief(type: string) {
  if (type.includes("晴")) { return sunny; }
  if (type.includes("阴")) { return cloudy; }
  if (type.includes("雪")) { return snow; }
  if (type.includes("多云")) { return cloudySun; }
  if (type.includes("小雨")) { return rainLight; }
  if (type.includes("中雨")) { return rainMedium; }
  if (type.includes("大雨")) { return rainHeavy; }
  if (type.includes("台风")) { return typhoon; }
  if (type.includes("沙尘暴")) { return sandStorm; }
  return cloudySun;
}
// 判断降水量大小的类型
function judgePrecipitationType(type: number) {
  if (type <= 0) { return precipitation; }
  if (type <= 10) { return precipitation01; }
  if (type <= 25) { return precipitation02; }
  if (type <= 50) { return precipitation03; }
  return precipitation04;
}

type WeatherChartProps= ChartProps<5, 4> & ColorProps<GlobalColorPalette> & ICSSProps;

/**
 * 天气预报卡片
 * @id zcxvnoinorwer
 * @author 马俊峰
 * @date 2020-01-17
 * @export
 * @class WeatherChart
 * @extends {PureComponent<WeatherChartProps>}
 * @implements {Chart<5, 4>}
 * @implements {Color}
 */
export class WeatherChart extends PureComponent<WeatherChartProps> implements Chart<5, 4>, Color, ICSS {
  render() {
    const { chartData: { data, range, suffix }, colorPalette, className, style } = this.props;
    if (data.length < 1) { return "data数据为空！"; }
    const xData = data.map(item => item[1]);
    const maxTam = Math.max(...range[6], ...data.map(item => item[6]));
    const minTam = Math.min(...range[7], ...data.map(item => item[7]));
    const maxTamData = data.map(item => item[6]);
    const minTamData = data.map(item => item[7]);
    const chartOption = this.getChartOption(xData, maxTamData, minTamData, maxTam, minTam);
    return (
      <WeatherChartBody colorPalette={colorPalette} style={style} className={className}>
        <div className="weather-border3">
          {data.map((item, index) => (
            <div className="wb-item" key={index}>
              <span className="date">{item[2]}</span>
              <span className="w-icon">
                <img src={judgeWeatherBrief(item[3])} alt="" className="w-img"/>
              </span>
              <span className="tam">{item[6]}°</span>
              <span className="tam chart-height">{item[7]}°</span>
              <span className="pre-label">
                <img src={judgePrecipitationType(item[8])} alt="" className="w-img"/>
              </span>
              {/* <span className="w-label">{item[4]}</span> */}
              <span className="w-label">风级</span>
              <span className="w-value">{item[5].substr(item[5].indexOf("转") + 1)}</span>
              <span className="w-label">湿度</span>
              <span className="w-value">{item[9]}%</span>
            </div>
          ))}
          <div className="weather-chart">
            <ReactEcharts notMerge={false} style={{ height: "100%", width: "100%" }} option={chartOption} />
          </div>
        </div>
      </WeatherChartBody>
    );
  }
  /** 获取图表的默认option */
  getChartOption = (xData: string[], maxTamData: number[], minTamData: number[], maxTam: number, minTam: number) => {
    const defaultOption = {
      grid: {
        left: 0,
        right: 0,
        top: 18,
        bottom: 0,
        containLabel: true,
      },
      xAxis: {
        show: false,
        // position: "top",
        // type: "category",
        // axisLine: { show: false },
        // splitLine: { show: false },
        // axisLabel: { color: "#0c193d" },
        // axisTick: { show: false },
        // boundaryGap: ["20%", "20%"],
        // data: ["周一", "周二", "周三", "周四", "周五", "周六", "周日"],
        data: xData,
      },
      yAxis: {
        type: "value",
        show: true,
        axisLine: { show: false },
        min: minTam - 0,
        max: maxTam + 0,
        position: "left",
        splitLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
      },
      series: [
        {
          name: "最高温",
          type: "line",
          // symbolSize: 0.00001,
          silent: true,
          smooth: true,
          symbol: "emptyCircle",
          symbolSize: 6,
          label: {
            normal: {
              show: false,
              formatter: "{c}°",
              position: "top",
              color: "#0c193d",
            },
          },
          lineStyle: { width: 1, color: "#fb496f" },
          data: maxTamData,
        },
        {
          name: "最低温",
          type: "line",
          // symbolSize: 0.00001,
          silent: true,
          smooth: true,
          symbol: "emptyCircle",
          symbolSize: 6,
          label: {
            normal: {
              show: false,
              formatter: "{c}°",
              position: "bottom",
              color: "#0c193d",
            },
          },
          lineStyle: { width: 1, color: "#4999f4" },
          data: minTamData,
        },
      ],
    };
    return defaultOption;
  }
}
